import * as echarts from 'echarts'
import { getElColorPrimary } from '@/utils/theme-color'
import { generateTooltip } from '@/utils/tool'
import { getEchartsFontFamily } from '@/utils/echarts-option/font'
import { nextTick } from 'vue'

/**
 * 让坐标轴的标签具有tooltip交互，
 * 适用于标签过长显示不完整时，悬停后展示全部在tooltip里
 * 使用此函数的前提条件：
 * 2、对应坐标轴的需配置为可触发事件，如：triggerEvent: true
 * 3、坐标轴的原标签需根据实际宽度限制展示宽度，展示不完全的使用'...'等展示，如：axisLabel: { formatter: (value) => { return value.length > 14 ? `${value.slice(0, 14)}...` : value } }
 * @param chartInstance 图表实例
 * @param axis 指定坐标轴
 */
export function useAxisLabelTooltip (chartInstance: echarts.EChartsType, axis: 'x'|'y' = 'x') {
  const axisKey = axis === 'x' ? 'xAxis' : 'yAxis'
  const tooltip = generateTooltip()
  chartInstance.on('mousemove', axisKey, (params) => {
    const {pageX, pageY} = params.event.event as any
    tooltip.innerHTML = params.value as string
    tooltip.style.left = `${pageX}px`
    tooltip.style.top = `${pageY + 10}px`
    tooltip.style.display = 'block'
    if (!document.body.contains(tooltip)) {
      document.body.appendChild(tooltip)
    }
  })
  chartInstance.on('mouseout', axisKey, function (params) {
    tooltip.style.display = 'none'
  })
  chartInstance.on('click', axisKey, function (params) {
    tooltip?.parentNode?.removeChild(tooltip)
  })
  const interval = setInterval(() => {
    if (!(chartInstance as any)._dom) {
      tooltip?.parentNode?.removeChild(tooltip)
      clearInterval(interval)
    }
  }, 2000)
}

/**
 * 让坐标轴的标签具有hover高亮交互
 * 使用此函数的前提条件：
 * 2、对应坐标轴的需配置为可触发事件，如：triggerEvent: true
 * @param chartInstance 图表实例
 * @param axis 指定坐标轴
 */
export function useAxisLabelHover (chartInstance: echarts.EChartsType, axis: 'x'|'y' = 'x') {
  const axisKey = axis === 'x' ? 'xAxis' : 'yAxis'
  chartInstance.on('mousemove', axisKey, (params) => {
    const option = chartInstance.getOption()
    option[axisKey][0].data[params.dataIndex] = {
      value: params.value,
      textStyle: {
        fontFamily: getEchartsFontFamily(),
        color: getElColorPrimary(),
      },
    }
    chartInstance.setOption(option)
  })
  chartInstance.on('mouseout', axisKey, function (params) {
    const option = chartInstance.getOption()
    option[axisKey][0].data[params.dataIndex] = params.value
    chartInstance.setOption(option)
  })
}

/**
 * echarts在横向柱状图时，会有柱子遮挡y轴的异常，这里做下补丁
 * 解决方案：利用此时echarts将y轴是绘画在g下的第一个path，方案仍具有不稳定性
 * 使用此函数的前提条件：
 * 1、图表采用svg渲染方式，如：echarts.init(chartNode.value, null, { renderer: 'svg' })
 * @param chartNode 图标的dom节点
 */
export function useYaxisTop (chartNode: Node & HTMLElement) {
  nextTick(() => {
    const yAxis = chartNode.querySelector('g > path')
    const g = yAxis.parentNode
    g.removeChild(yAxis)
    g.appendChild(yAxis)
  })
}
